import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft, Mail, Phone, Linkedin, Building2, Globe,
    Sparkles, Send, Trash2, Loader2, CheckCircle2, Eye,
    ThumbsUp, ThumbsDown, Target,
} from "lucide-react";
import { toast } from "sonner";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { leadsApi } from "@/lib/leadsApi";
import { extrasApi } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Avatar, ScoreBar, StatusBadge, TinyTag } from "@/components/find/atoms";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["new", "contacted", "engaged", "meeting", "won", "lost"];

export default function LeadDetail() {
    const { leadId } = useParams();
    const navigate = useNavigate();
    const { t, locale } = useI18n();
    const { refresh } = useAuth();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [noteText, setNoteText] = useState("");
    const [rating, setRating] = useState(null); // "up" | "down" | null
    const [ratingBusy, setRatingBusy] = useState(false);
    const [whyMatch, setWhyMatch] = useState(null);
    const [whyBusy, setWhyBusy] = useState(false);

    useEffect(() => {
        setLoading(true);
        leadsApi.get(leadId)
            .then((l) => {
                setLead(l);
                if (l.why_match) setWhyMatch(l.why_match);
            })
            .catch(() => toast.error("Lead not found"))
            .finally(() => setLoading(false));
        extrasApi.ratings.get(leadId).then((r) => setRating(r?.rating || null)).catch(() => {});
    }, [leadId]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 size={22} className="animate-spin text-zinc-500" />
            </div>
        );
    }
    if (!lead) return null;

    async function handleReveal(field) {
        try {
            const res = await leadsApi.reveal(lead.lead_id, field);
            setLead(res.lead);
            refresh();
            toast.success(`${field === "email" ? "Email" : "Phone"} revealed`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    }

    async function saveNote() {
        if (!noteText.trim()) return;
        try {
            const updated = await leadsApi.update(lead.lead_id, { note: noteText.trim() });
            setLead(updated);
            setNoteText("");
            toast.success("Note saved");
        } catch (_e) {
            toast.error("Failed");
        }
    }

    async function changeStatus(v) {
        try {
            const updated = await leadsApi.update(lead.lead_id, { status: v });
            setLead(updated);
            toast.success("Status updated");
        } catch (_e) {
            toast.error("Failed");
        }
    }

    async function handleDelete() {
        if (!window.confirm(t.find.detail.deleteConfirm)) return;
        try {
            await leadsApi.remove(lead.lead_id);
            toast.success(t.find.detail.deleted);
            navigate("/app/find/leads");
        } catch (_e) {
            toast.error("Failed");
        }
    }

    async function handleRate(newRating) {
        if (ratingBusy) return;
        setRatingBusy(true);
        try {
            let reason = null;
            if (newRating === "down" && rating !== "down") {
                reason = window.prompt(
                    locale === "fr"
                        ? "Pourquoi ce lead ne convient pas ? (fake email, mauvais rôle, entreprise fermée…) — optionnel"
                        : "Why is this lead bad? (fake email, wrong role, closed company…) — optional",
                    "",
                );
                if (reason === null) { setRatingBusy(false); return; }  // user cancelled
            }
            const res = await extrasApi.ratings.rate(lead.lead_id, newRating, reason || undefined);
            setRating(res.rating);
            refresh();
            if (res.credits_refunded > 0) {
                toast.success(
                    locale === "fr"
                        ? `Merci ! ${res.credits_refunded} crédits remboursés (${res.refund_details.join(", ")})`
                        : `Thanks! ${res.credits_refunded} credits refunded (${res.refund_details.join(", ")})`,
                );
            } else if (newRating === "down" && res.capped) {
                toast.warning(locale === "fr" ? "Plafond mensuel de remboursement atteint (20%)" : "Monthly refund cap reached (20%)");
            } else {
                toast.success(newRating === "up" ? "👍" : "👎");
            }
            // Reload to see the timeline update
            const fresh = await leadsApi.get(lead.lead_id);
            setLead(fresh);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setRatingBusy(false);
        }
    }

    async function generateWhyMatch() {
        if (whyBusy) return;
        setWhyBusy(true);
        try {
            const r = await extrasApi.whyMatch(lead.lead_id);
            setWhyMatch(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI unavailable");
        } finally {
            setWhyBusy(false);
        }
    }

    const radarData = Object.entries(lead.score_breakdown || {}).map(([k, v]) => ({
        metric: k.charAt(0).toUpperCase() + k.slice(1),
        value: v,
    }));

    const aiSummary = buildAiSummary(lead);

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <Link to="/app/find/leads" data-testid="lead-detail-back" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition">
                    <ArrowLeft size={14} /> {t.find.detail.back}
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* LEFT column */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Profile header */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative rounded-2xl p-6 border border-white/8 overflow-hidden"
                        style={{
                            background: "radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.15), transparent 60%), radial-gradient(ellipse at 100% 100%, rgba(6,182,212,0.12), transparent 55%), #1A1A2E",
                        }}
                    >
                        <div className="flex items-start gap-4 flex-wrap">
                            <Avatar seed={lead.avatar_seed} size={64} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter" data-testid="lead-detail-name">{lead.full_name}</h1>
                                    <StatusBadge status={lead.status} label={t.find.statuses[lead.status] || lead.status} />
                                </div>
                                <div className="mt-1 text-zinc-400">{lead.title} · {lead.company_name}</div>
                                <div className="mt-1 text-sm text-zinc-500">{lead.city}, {lead.country} · {t.find.seniorities[lead.seniority]} · {lead.industry}</div>
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {(lead.tags || []).map((tg) => <TinyTag key={tg}>{tg}</TinyTag>)}
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <Select value={lead.status} onValueChange={changeStatus}>
                                    <SelectTrigger className="w-[140px] h-9 bg-brand-void border-white/10" data-testid="lead-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-brand-surface border-white/10 text-white">
                                        {STATUSES.map((s) => (
                                            <SelectItem key={s} value={s}>{t.find.statuses[s]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="flex items-center gap-1.5 mt-1">
                                    <button
                                        type="button"
                                        onClick={() => handleRate("up")}
                                        disabled={ratingBusy}
                                        data-testid="lead-rate-up"
                                        className={`w-8 h-8 rounded-lg inline-flex items-center justify-center border transition ${
                                            rating === "up"
                                                ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300"
                                                : "border-white/10 text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/40"
                                        }`}
                                        title={locale === "fr" ? "Bon lead" : "Good lead"}
                                    >
                                        <ThumbsUp size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleRate("down")}
                                        disabled={ratingBusy}
                                        data-testid="lead-rate-down"
                                        className={`w-8 h-8 rounded-lg inline-flex items-center justify-center border transition ${
                                            rating === "down"
                                                ? "bg-rose-500/20 border-rose-500/50 text-rose-300"
                                                : "border-white/10 text-zinc-400 hover:text-rose-300 hover:border-rose-500/40"
                                        }`}
                                        title={locale === "fr" ? "Mauvais lead → remboursement" : "Bad lead → refund"}
                                    >
                                        <ThumbsDown size={13} />
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    data-testid="lead-delete"
                                    className="text-xs text-rose-400 hover:text-rose-200 inline-flex items-center gap-1"
                                >
                                    <Trash2 size={12} /> Delete
                                </button>
                            </div>
                        </div>
                    </motion.div>

                    {/* AI summary */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center">
                                <Sparkles size={14} className="text-white" />
                            </div>
                            <div className="text-sm font-semibold">{t.find.detail.aiSummary}</div>
                        </div>
                        <p className="text-zinc-200 leading-relaxed" data-testid="lead-ai-summary">{aiSummary}</p>
                    </div>

                    {/* Why this lead matches (AI justification) */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-cyan to-brand-indigo flex items-center justify-center">
                                    <Target size={14} className="text-white" />
                                </div>
                                <div className="text-sm font-semibold">
                                    {locale === "fr" ? "Pourquoi ce lead te correspond" : "Why this lead matches"}
                                </div>
                            </div>
                            {!whyMatch && (
                                <button
                                    type="button"
                                    onClick={generateWhyMatch}
                                    disabled={whyBusy}
                                    data-testid="lead-why-match-generate"
                                    className="text-xs font-medium px-3 py-1.5 rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/20 inline-flex items-center gap-1.5"
                                >
                                    {whyBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                    {locale === "fr" ? "Générer" : "Generate"}
                                </button>
                            )}
                        </div>
                        {whyMatch ? (
                            <div>
                                <p className="text-zinc-200 leading-relaxed" data-testid="lead-why-match-text">{whyMatch.why_fr}</p>
                                {(whyMatch.signals || []).length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {whyMatch.signals.map((s, i) => (
                                            <span key={i} className="text-[10px] font-mono px-2 py-1 rounded-md bg-brand-cyan/10 text-brand-cyan border border-brand-cyan/25">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-zinc-500">
                                {locale === "fr"
                                    ? "Génère une explication IA en 2 lignes (basé sur seniorité, secteur, taille, région). Gratuit."
                                    : "Generate a 2-line AI explanation (based on seniority, industry, size, region). Free."}
                            </p>
                        )}
                    </div>

                    {/* Timeline */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                        <div className="text-sm font-semibold mb-4">{t.find.detail.timeline}</div>
                        <ol className="space-y-4">
                            {[...(lead.activities || [])].reverse().map((a, i) => (
                                <li key={i} className="flex gap-3">
                                    <div className="mt-1 w-2 h-2 rounded-full bg-brand-cyan shrink-0" />
                                    <div className="flex-1">
                                        <div className="text-sm text-zinc-200">{a.description}</div>
                                        <div className="text-xs text-zinc-500 font-mono">{formatDate(a.timestamp)}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>

                    {/* Notes */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                        <div className="text-sm font-semibold mb-3">{t.find.detail.notes}</div>
                        {(lead.notes || []).length === 0 && (
                            <div className="text-sm text-zinc-500 mb-3">No notes yet.</div>
                        )}
                        <div className="space-y-3 mb-4">
                            {(lead.notes || []).map((n, i) => (
                                <div key={i} className="bg-brand-void/60 rounded-lg p-3 border border-white/5">
                                    <div className="text-sm text-zinc-100">{n.text}</div>
                                    <div className="mt-1 text-[11px] text-zinc-500 font-mono">{n.author} · {formatDate(n.timestamp)}</div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && saveNote()}
                                placeholder={t.find.detail.addNote}
                                data-testid="lead-note-input"
                                className="flex-1 px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                            />
                            <button
                                type="button"
                                onClick={saveNote}
                                data-testid="lead-note-save"
                                className="btn-cta text-white text-sm font-semibold px-4 rounded-lg"
                            >
                                {t.find.detail.addNoteBtn}
                            </button>
                        </div>
                    </div>
                </div>

                {/* RIGHT column */}
                <div className="space-y-6">
                    {/* Score */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-semibold">{t.find.detail.score}</div>
                            <div className="font-display font-black text-2xl text-white" data-testid="lead-score">{lead.score}</div>
                        </div>
                        <ScoreBar score={lead.score} />
                        <div className="mt-4 h-52 min-h-[200px] w-full">
                            <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                                <RadarChart data={radarData}>
                                    <PolarGrid stroke="rgba(255,255,255,0.08)" />
                                    <PolarAngleAxis dataKey="metric" tick={{ fill: "#A1A1AA", fontSize: 10 }} />
                                    <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                                    <Radar dataKey="value" stroke="#6366F1" fill="#6366F1" fillOpacity={0.35} />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                        <div className="text-sm font-semibold mb-3">{t.find.detail.info}</div>
                        <RevealField
                            icon={Mail}
                            valueRevealed={lead.email}
                            valueHidden={lead.email_hidden}
                            label={lead.email ? lead.email : lead.email_hidden}
                            cost={1}
                            onReveal={() => handleReveal("email")}
                            testId="lead-reveal-email"
                        />
                        <RevealField
                            icon={Phone}
                            valueRevealed={lead.phone}
                            valueHidden={lead.phone_hidden}
                            label={lead.phone ? lead.phone : lead.phone_hidden}
                            cost={2}
                            onReveal={() => handleReveal("phone")}
                            testId="lead-reveal-phone"
                        />
                        <a
                            href={lead.linkedin}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 flex items-center gap-2 text-sm text-zinc-200 hover:text-white transition"
                        >
                            <Linkedin size={14} className="text-brand-cyan" />
                            <span className="truncate">LinkedIn profile</span>
                        </a>
                    </div>

                    {/* Company */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                        <div className="text-sm font-semibold mb-3">{t.find.detail.company}</div>
                        <div className="flex items-center gap-2 text-sm text-zinc-200"><Building2 size={14} className="text-zinc-500" /> {lead.company_name}</div>
                        {lead.company_domain && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-zinc-400"><Globe size={14} className="text-zinc-500" /> {lead.company_domain}</div>
                        )}
                        {lead.address && (
                            <div className="mt-2 text-xs text-zinc-400 font-mono">{lead.address}</div>
                        )}
                        <div className="mt-2 text-xs text-zinc-500">
                            {lead.industry} · {lead.company_size} · Founded {lead.founded_year} · {lead.funding_stage}
                        </div>
                        {(lead.siren || lead.siret) && (
                            <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 gap-2 text-[11px]">
                                {lead.siren && <div><div className="text-zinc-500 uppercase tracking-widest text-[9px] font-bold">SIREN</div><div className="font-mono text-zinc-200">{lead.siren}</div></div>}
                                {lead.siret && <div><div className="text-zinc-500 uppercase tracking-widest text-[9px] font-bold">SIRET</div><div className="font-mono text-zinc-200">{lead.siret}</div></div>}
                                {lead.postal_code && <div><div className="text-zinc-500 uppercase tracking-widest text-[9px] font-bold">Postal</div><div className="font-mono text-zinc-200">{lead.postal_code}</div></div>}
                                {lead.naf_code && <div><div className="text-zinc-500 uppercase tracking-widest text-[9px] font-bold">NAF</div><div className="font-mono text-zinc-200">{lead.naf_code}</div></div>}
                            </div>
                        )}
                        {lead.data_source && (
                            <div className="mt-3 text-[10px] font-mono text-brand-cyan">source: {lead.data_source}</div>
                        )}
                        <div className="mt-4">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{t.find.detail.techStack}</div>
                            <div className="flex flex-wrap gap-1.5">
                                {(lead.tech_stack || []).map((tech) => <TinyTag key={tech}>{tech}</TinyTag>)}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="rounded-2xl p-6 bg-brand-surface border border-white/8 space-y-2">
                        <Link
                            to="/app/engage"
                            className="w-full text-center btn-cta text-white text-sm font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2"
                        >
                            <Send size={14} /> {t.find.detail.pushSequence}
                        </Link>
                        <Link
                            to="/app/copilot"
                            className="w-full text-center text-sm font-medium py-2.5 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 text-white inline-flex items-center justify-center gap-2"
                        >
                            <Sparkles size={14} /> {t.find.detail.composeEmail}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RevealField({ icon: Icon, valueRevealed, label, cost, onReveal, testId }) {
    return (
        <div className="flex items-center gap-2 py-1.5">
            <Icon size={14} className="text-zinc-500 shrink-0" />
            <span className={`flex-1 text-sm truncate ${valueRevealed ? "text-white font-mono" : "text-zinc-500"}`}>
                {label}
            </span>
            {valueRevealed ? (
                <span className="text-[10px] font-mono text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle2 size={11} /> Revealed
                </span>
            ) : (
                <button
                    type="button"
                    onClick={onReveal}
                    data-testid={testId}
                    className="text-[11px] font-mono badge-cyan px-2 py-1 rounded-md hover:opacity-90 inline-flex items-center gap-1"
                >
                    <Eye size={11} /> {cost}c
                </button>
            )}
        </div>
    );
}

function buildAiSummary(l) {
    const pieces = [];
    pieces.push(`${l.full_name} is ${l.title} at ${l.company_name} (${l.industry}, ${l.company_size} people, ${l.city} ${l.country}).`);
    if (l.score >= 80) pieces.push(`Score ${l.score} — high-fit prospect worth a personal outreach.`);
    else if (l.score >= 60) pieces.push(`Score ${l.score} — mid-fit, warm intro or a strong hook needed.`);
    else pieces.push(`Score ${l.score} — nurture only or reconsider ICP fit.`);
    if (l.tech_stack?.length) pieces.push(`Tech signals: ${l.tech_stack.slice(0, 4).join(", ")}.`);
    if (l.funding_stage && l.funding_stage !== "bootstrapped" && l.funding_stage !== "public") pieces.push(`Recent funding stage: ${l.funding_stage}.`);
    return pieces.join(" ");
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString();
    } catch (_e) {
        return iso;
    }
}
